let _ = t => t,
  _t,
  _t2;
/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getProductBundles, getPublicProductBundles } from 'pricing-pages-lib/api/ProductBundlesApi';
import { createProductConfig, mergeSelectedAndBundledProductConfigurations } from 'pricing-pages-lib/utils/productConfigUtils';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import breakpoint from 'pricing-pages-ui/constants/Breakpoints';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { INTERSTITIAL } from '../../constants/tracking/Locations';
import { PlanBuilderContext } from '../../context/PlanBuilderContext';
import { useSortedProductConfigurations } from '../../hooks/useSortedProductConfigurations';
import { useAppSelector } from '../../reducers';
import { isAppAuthSelector } from '../../selectors/AuthSelectors';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { commonAllowedQueryParamTermSelector } from '../../selectors/OfferSelectors';
import { checkoutProductConfigurationsSelector, configsWithSuiteIfPossibleSelector, productConfigurationForSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasProductVersionOrHigherProductLevelInProductInventorySelector, ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, isQuantifiableHubSelector, productForSkuIdSelector, skuIdIsHubSelector, skuIdIsPartOfCrmSuiteSelector } from '../../selectors/ProductsSelectors';
import { productDataSelector } from '../../selectors/SelectorUtils';
import { queryParamProductConfigurationsSelector, useProductConfigsQueryParamsParamsEffect, useQueryParamProductConfigsOrDefault } from '../../utils/planBuilderUtils/PlanBuilderQueryParamUtils';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "PlanBuilder__Wrapper",
  componentId: "oo4rar-0"
})(["width:100%;margin:0 auto;padding:0;", ";"], breakpoint.phone(_t || (_t = _`
    width: inherit;
  `)));
const ResponsiveUIFlex = styled(UIFlex).withConfig({
  displayName: "PlanBuilder__ResponsiveUIFlex",
  componentId: "oo4rar-1"
})(["", ";"], breakpoint.cs(_t2 || (_t2 = _`
    flex-direction: column;

    .flex-column {
      margin: 0 auto;
    }
  `)));
const PlanBuilderContent = ({
  initialProductConfigurations = [],
  children,
  trackingSource,
  screen,
  allowUpgradesToCrmSuite
}) => {
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const [isLoading, setIsLoading] = useState(false);
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const productData = useAppSelector(productDataSelector);
  const queryParams = getQueryParams();
  const queryParamProductConfigurations = useAppSelector(state => queryParamProductConfigurationsSelector(state, {
    queryParams
  }));
  const queryParamApiNames = useAppSelector(state => {
    return queryParamProductConfigurations.map(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product && product.apiName;
    });
  });
  const initialProductConfigurationsUsingQueryParams = useQueryParamProductConfigsOrDefault({
    defaultConfigurations: initialProductConfigurations,
    requiredProductLines: []
  });
  const initialSuiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: initialProductConfigurationsUsingQueryParams.map(({
      skuId
    }) => skuId)
  }));
  const [suiteProductLevel, setSuiteProductLevel] = useState(initialSuiteProductLevel);
  const selectedOwnedSuiteProductLevel = useSelector(state => {
    if (!suiteProductLevel) return false;
    return ownsCrmSuiteProductLevel(state, {
      productLevel: suiteProductLevel
    });
  });
  const filteredInitialProductConfigurations = useAppSelector(state => {
    return initialProductConfigurationsUsingQueryParams.filter(({
      skuId
    }) => {
      const allowedForCrmSuiteUpgrade = suiteProductLevel && skuIdIsPartOfCrmSuiteSelector(state, {
        skuId,
        suiteProductLevel: suiteProductLevel
      });
      return allowedForCrmSuiteUpgrade || isQuantifiableHubSelector(state, {
        skuId
      }) || !(skuIdIsHubSelector(state, {
        skuId
      }) && hasProductVersionOrHigherProductLevelInProductInventorySelector(state, {
        skuId
      }));
    });
  });
  const productConfigsWithSeatsDefaults = useAppSelector(state => productConfigurationForSeatsSelector(state, {
    productConfigurations: filteredInitialProductConfigurations
  }));
  const [originalProductConfigurations, setProductConfigurations] = useSortedProductConfigurations(productConfigsWithSeatsDefaults);

  // On some pages we insert the rest of CRM suite if you own the remaining plans needed for it already
  const productConfigurations = useAppSelector(state => {
    let productConfigs = [...originalProductConfigurations];
    if (allowUpgradesToCrmSuite) {
      productConfigs = configsWithSuiteIfPossibleSelector(state, {
        productConfigurations: productConfigs
      });
    }
    return productConfigs;
  });
  const memoizedProductConfigurations = useMemo(() => productConfigurations, [originalProductConfigurations]);
  useEffect(() => {
    // only run this useEffect on page load
    const getProductBundlesCall = isAppAuth ? getProductBundles : getPublicProductBundles;

    // if the user came to the bundles page with no query params set,
    // no need to call the bundles API
    if (queryParamApiNames !== null && queryParamApiNames !== void 0 && queryParamApiNames.length) {
      setIsLoading(true);
      getProductBundlesCall(queryParamApiNames).then(response => {
        const newProducts = response.products;
        const newProductConfigurations = newProducts.map(({
          skuId
        }) => {
          return createProductConfig({
            skuId
          });
        });
        const mergedSelectedAndBundledProductConfigurations = mergeSelectedAndBundledProductConfigurations({
          productData
        }, {
          bundleResponseProductConfigs: newProductConfigurations,
          selectedProductConfigs: productConfigurations
        });
        setProductConfigurations(mergedSelectedAndBundledProductConfigurations);
      }).catch(err => {
        // Sentry logged in bundles API call
        console.error(err);
      }).finally(() => {
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useProductConfigsQueryParamsParamsEffect(memoizedProductConfigurations);
  const isGoingToInterstitial = isUngatedForSeats && trackingSource !== INTERSTITIAL;
  const checkoutProductConfigurations = useAppSelector(state => {
    const checkoutConfigs = checkoutProductConfigurationsSelector(state, {
      productConfigurations,
      subtractOwnedUnitsIfBuyingNoAdditional: true,
      subtractOwnedSuiteProducts: selectedOwnedSuiteProductLevel && !isGoingToInterstitial
    });
    if (checkoutConfigs.length === 0) return [];
    return checkoutConfigs;
  });

  // For the Cart/SummaryColumn, we want to display all checkout configurations
  // except onboarding
  const cartDisplayProductConfigurations = useAppSelector(state => {
    const checkoutConfigs = checkoutProductConfigurationsSelector(state, {
      productConfigurations,
      subtractOwnedUnitsIfBuyingNoAdditional: true,
      subtractOwnedSuiteProducts: selectedOwnedSuiteProductLevel && !isGoingToInterstitial,
      includeOnboarding: false
    });
    if (checkoutConfigs.length === 0) return [];
    return checkoutConfigs;
  });

  // For the price breakdown modal, we want to display all checkout configurations
  // with onboarding as a line item if it's required
  const breakdownDisplayProductConfigurations = useAppSelector(state => {
    return checkoutProductConfigurationsSelector(state, {
      productConfigurations,
      subtractOwnedUnitsIfBuyingNoAdditional: true,
      subtractOwnedSuiteProducts: selectedOwnedSuiteProductLevel && !isGoingToInterstitial,
      includeOnboarding: true,
      includeOnboardingFallback: false
    });
  });
  const {
    queryParamTerm,
    setQueryParamTerm
  } = useQueryParamTerm();
  const commonAllowedQueryParamTerm = useAppSelector(state => {
    return commonAllowedQueryParamTermSelector(state, {
      skuIds: checkoutProductConfigurations.map(config => config.skuId),
      selectedTerm: queryParamTerm
    });
  });
  return /*#__PURE__*/_jsx(PlanBuilderContext.Provider, {
    value: {
      productConfigurations: memoizedProductConfigurations,
      setProductConfigurations,
      queryParamTerm: commonAllowedQueryParamTerm,
      setQueryParamTerm: newTerm => setQueryParamTerm(newTerm),
      checkoutProductConfigurations,
      trackingSource,
      screen,
      setSuiteProductLevel,
      cartDisplayProductConfigurations,
      breakdownDisplayProductConfigurations,
      setIsLoading,
      isLoading,
      showDiscountedPricesInUnitsPicker: false,
      requiredProductLines: []
    },
    children: /*#__PURE__*/_jsx(Wrapper, {
      children: /*#__PURE__*/_jsx(ResponsiveUIFlex, {
        children: children
      })
    })
  });
};
const PlanBuilder = props => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  if (hasPricingAndPurchasingSucceeded) {
    return /*#__PURE__*/_jsx(PlanBuilderContent, Object.assign({}, props));
  } else {
    return /*#__PURE__*/_jsx("div", {
      style: {
        minHeight: 600,
        width: '100%'
      },
      className: "flex-column justify-center align-center",
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: "medium"
      })
    });
  }
};
export default PlanBuilder;